// src/features/views/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { Diff } from "@/features/FormCard/Diff";
import { AutocompleteAsyncField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

// static identifiers

// Zod schema for create‐new form
const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  sort_order: z.coerce.number().optional().nullable(),
  content_url: z.string().min(1, "Content URL is required"),
  // AutocompleteAsyncField for workbook lookup (both display and ID)
  workbook_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  workbook_id: z.coerce.number().optional().nullable(),
});

export function ViewEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id,
    client,
    table_name,
    FormSchema,
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "workbook_name") {
        return {
          id: fetchedData.workbook_id,
          name: value,
          text: fetchedData.workbook_name,
        };
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                View {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Name & Sort Order */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`${table_name}.columns.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="workbook_name"
                    label={t(`${table_name}.columns.workbook_name`, {
                      ns: "tables",
                      defaultValue: "Workbook",
                    })}
                    base_url="tableau-quantilia/workbooks"
                    column_name="name"
                    id_column="workbook_id"
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* Row 2: Content URL & Workbook selector */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="content_url"
                    label={t(`${table_name}.columns.content_url`, {
                      ns: "tables",
                      defaultValue: "Content URL",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name="sort_order"
                    label={t(`${table_name}.columns.sort_order`, {
                      ns: "tables",
                      defaultValue: "Sort Order",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={id}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirm New‐item Modal */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["workbook_id"])
              : "No changes detected"}
          </ConfirmationModal>

          {/* Cancel (leave) Confirmation Modal */}
          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
